import { Fragment, useEffect, useReducer } from "react";
import jalMoment from "moment-jalaali";
import { jalCalendarHeader, jalMonthNames } from "../../../scripts/helpers";
import { ReactComponent as ArrowRight } from "../../../assets/images/icons/arrow-right.svg";
import { ReactComponent as ArrowLeft } from "../../../assets/images/icons/arrowLeft.svg";
import Dropdown from "../dropdown/dropDown.index";
import moment from "moment-jalaali";
import { Calendar2Props } from "./calendar2.interface";

function generateArrayOfYears() {
    let lastYear = Number(jalMoment(new Date()).format("jYYYY")) + 1;
    let firstYear = lastYear - 10;
    let years = [];

    for (let i = lastYear; i >= firstYear; i--) years.push(i);

    return years;
}

const years = generateArrayOfYears();

const monthOptions: any = [];
const yearOptions: any = [];

jalMonthNames.map((month, index) => monthOptions.push({ label: month, payload: index + 1 }));
years.map((year) => yearOptions.push({ label: year, payload: year }));

const checkDayOfWeek = (date: string, format: string) => {
    const dayInGre = Number(jalMoment(date, format).day());
    let dayInJal;

    if (dayInGre === 6 || dayInGre === 7) dayInJal = dayInGre - 5;
    else dayInJal = dayInGre + 2;

    return dayInJal;
};

const convertJalDateToGreDate = (date: string) => jalMoment(date, "jYYYY/jMM/jDD").format("YYYY/MM/DD");

const createDaysOfMonth = (month: number, year: number) => {
    const firstDay = checkDayOfWeek(`${year}/${month}/1`, "jYYYY/jMM/jD");
    const lastDay = Number(jalMoment(`${year}/${month}/1`, "jYYYY/jMM/jD").endOf("jMonth").format("jDD"));
    let list = [];
    for (let i = 1; i <= lastDay; i++) {
        const dayInGre = convertJalDateToGreDate(`${year}/${month}/${i}`);
        const timestamp = jalMoment(dayInGre).startOf("day").unix();
        list.push(timestamp);
    }
    const newArray = new Array(firstDay - 1).fill(null);
    newArray.push(...list);

    return newArray;
};

const getDayFromTimestamp = (timestamp: number) => Number(jalMoment.unix(timestamp).format("jD"));

interface CalendarStateType {
    selectedDay: number | null;
    year: number;
    month: number;
    today: number;
    days: number[];
}

enum CalendarActions {
    SET_SELECTED_DATE,
    SET_YEAR,
    SET_MONTH,
    SET_TODAY,
    SET_DAYS
}

const calendarReducerInitialValue: CalendarStateType = {
    month: Number(jalMoment().format("jM")),
    year: Number(jalMoment().format("jYYYY")),
    selectedDay: null,
    days: [],
    today: Number(jalMoment().startOf("day").unix())
};

function calendarReducer(state: CalendarStateType, action: { payload: any; type: CalendarActions }): any {
    switch (action.type) {
        case CalendarActions.SET_YEAR:
            return { ...state, year: action.payload };
        case CalendarActions.SET_MONTH:
            return { ...state, month: action.payload };
        case CalendarActions.SET_SELECTED_DATE:
            return { ...state, selectedDay: action.payload };
        case CalendarActions.SET_DAYS:
            return { ...state, days: action.payload };
        default:
            return new Error(`Unhandled  action type ${action.type}`);
    }
}

export default function Calendar(props: Calendar2Props): JSX.Element {
    const [state, dispatch] = useReducer(calendarReducer, calendarReducerInitialValue);

    useEffect(() => {
        if (props.selectedDay) {
            dispatch({ type: CalendarActions.SET_SELECTED_DATE, payload: props.selectedDay });
        }
    }, [props.selectedDay]);

    useEffect(() => {
        props.onChange(state.selectedDay);
    }, [state.selectedDay]);

    useEffect(() => {
        dispatch({ type: CalendarActions.SET_DAYS, payload: createDaysOfMonth(state.month, state.year) });
    }, [state.year, state.month]);

    const handlePreviousMonth = () => {
        if (state.month > 1) {
            dispatch({ type: CalendarActions.SET_MONTH, payload: state.month - 1 });
        } else {
            dispatch({ type: CalendarActions.SET_MONTH, payload: 12 });
            dispatch({ type: CalendarActions.SET_YEAR, payload: state.year - 1 });
        }
    };

    const handleNextMonth = () => {
        if (state.month < 12) {
            dispatch({ type: CalendarActions.SET_MONTH, payload: state.month + 1 });
        } else {
            if (state.year === years[0]) return;
            dispatch({ type: CalendarActions.SET_MONTH, payload: 1 });
            dispatch({ type: CalendarActions.SET_YEAR, payload: state.year + 1 });
        }
    };

    const handleSelectDay = (item: number) => {
        dispatch({ type: CalendarActions.SET_SELECTED_DATE, payload: item });
    };

    const handleDisabled = (item: number) => {
        if (props.disableAfter) {
            return props.disableAfter < item;
        }
        if (props.disableBefore) {
            return props.disableBefore > item;
        }
        return undefined;
    };

    return (
        <>
            <header className="flex w-full justify-center mt-4 sm:justify-between">
                <ArrowRight
                    className={`cursor-pointer opacity-90 ${state.month === 1 && state.year === years[years.length - 1] && "!opacity-10 !cursor-default"}`}
                    onClick={handlePreviousMonth}
                />
                <div className="flex w-full justify-center items-center mx-auto mt-1">
                    <Dropdown
                        options={monthOptions}
                        value={monthOptions[state.month - 1]}
                        onChange={(value) => dispatch({ type: CalendarActions.SET_MONTH, payload: value.payload })}
                        classNames="border-0 flex justify-center"
                        arrowDownType={2}
                        arrowDownClassNames="relative right-3"
                        optionsClassNames="w-auto right-[37px] sm:right-[0px] md:right-[28px]"
                    />
                    <Dropdown
                        options={yearOptions}
                        value={yearOptions.find((item: any) => item.payload === state.year)}
                        onChange={(value) => dispatch({ type: CalendarActions.SET_YEAR, payload: value.payload })}
                        classNames="border-0 flex justify-center"
                        arrowDownType={2}
                        arrowDownClassNames="relative right-3"
                        optionsClassNames="w-auto right-[44px] sm:right-[0px] md:right-[28px]"
                    />
                </div>
                <ArrowLeft
                    className={`cursor-pointer opacity-90 ${state.month === 12 && state.year === years[0] && "!opacity-10 !cursor-default"}`}
                    onClick={handleNextMonth}
                />
            </header>
            <div className="grid grid-cols-7 mt-5 text-secondary-600">
                {jalCalendarHeader.map((item, index) => (
                    <div key={index} className="flex flex-col justify-center items-center text-[11px] font-medium">
                        {item}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7 mt-2">
                {state.days.length > 0 &&
                    state.days?.map((item: number, index: number) =>
                        item ? (
                            <Fragment key={item}>
                                <button
                                    style={{ fontFamily: "IranYekanFa" }}
                                    className={`relative 2xl:justify-content text-[14px] font-[bold] aspect-square rounded-md ${item === moment.unix(state.selectedDay).startOf("day").unix() && "bg-primary-600 text-white"} ${item === state.today && "border-[1px] border-primary-600 sm:border-secondary-300 rounded-[10px]"} disabled:cursor-not-allowed disabled:text-secondary-400`}
                                    onClick={() => handleSelectDay(item)}
                                    disabled={handleDisabled(item)}
                                >
                                    {getDayFromTimestamp(item)}
                                </button>
                            </Fragment>
                        ) : (
                            <div className="w-full" key={index} />
                        )
                    )}
            </div>
        </>
    );
}
